import React, { useState, useEffect, useRef } from 'react';
import { roomsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socket';
import './RoomChat.css';

const RoomChat = ({ roomId }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadMessages();
    // Poll for new messages every 3 seconds
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [roomId]);

  // Écouter les nouveaux messages en temps réel via WebSocket
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket || !roomId) return;

    let seenIds = new Set();

    const handleRealtime = (data) => {
      const roomIdStr = String(roomId);
      const matchesId = data.id_meeting !== undefined && String(data.id_meeting) === roomIdStr;
      const matchesRoom = data.roomId !== undefined && String(data.roomId) === roomIdStr;
      if ((data.id_meeting !== undefined || data.roomId !== undefined) && !matchesId && !matchesRoom) return;

      const msgId = data.id || data.idmessage;
      if (msgId && seenIds.has(msgId)) return;
      if (msgId) seenIds.add(msgId);

      const msg = {
        id:        msgId           || data.idmessage,
        message:   data.message    || data.contenu,
        content:   data.message    || data.contenu,
        userId:    data.senderId   || data.iduser,
        userName:  data.senderName || (data.userPrenom && data.userName
                               ? `${data.userPrenom} ${data.userName}`.trim()
                               : (data.userName  || '')),
        createdAt: data.timestamp  || data.dateEnvoie,
        timestamp: data.timestamp  || data.dateEnvoie,
        dateEnvoie:data.timestamp  || data.dateEnvoie,
        userAvatar:data.userAvatar || null,
      };

      setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]);
    };

    socket.on('chat-message', handleRealtime);
    return () => { socket.off('chat-message', handleRealtime); };
  }, [roomId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await roomsAPI.getMessages(roomId);
      if (response.success) {
        const formattedMessages = response.data.map(msg => ({
          id: msg.idmessage,
          message: msg.contenu,
          userId: msg.iduser,
          userName: msg.userName,
          content: msg.contenu,
          dateEnvoie: msg.dateEnvoie,
          createdAt: msg.dateEnvoie,
          timestamp: msg.dateEnvoie,
          userAvatar: msg.userAvatar || null
        }));
        setMessages(formattedMessages);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await roomsAPI.sendMessage(roomId, newMessage.trim());
      if (response.success) {
        const msg = response.data;
        const newMsg = {
          id: msg.idmessage,
          message: msg.contenu,
          userId: msg.iduser,
          userName: msg.userName,
          createdAt: msg.dateEnvoie,
          timestamp: msg.dateEnvoie,
          userAvatar: msg.userAvatar || null,
          content: msg.contenu,
          dateEnvoie: msg.dateEnvoie
        };
        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = new Date(message.dateEnvoie).toDateString();
      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({
          date: message.dateEnvoie,
          messages: [message]
        });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  if (loading) {
    return (
      <div className="room-chat">
        <div className="chat-loading">Chargement des messages...</div>
      </div>
    );
  }

  return (
    <div className="room-chat">
      <div className="chat-header">
        <h3>💬 Chat de la salle</h3>
        <span className="message-count">{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>Aucun message dans cette salle.</p>
            <p>Soyez le premier à envoyer un message !</p>
          </div>
        ) : (
          messageGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="message-group">
              <div className="date-separator">
                <span>{formatDate(group.date)}</span>
              </div>
              {group.messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.userId === user?.id ? 'own' : 'other'}`}
                >
                  {message.userId !== user?.id && (
                    <div className="message-avatar">
                      {message.userAvatar ? (
                        <img src={message.userAvatar} alt={message.userName} />
                      ) : (
                        <div className="avatar-placeholder">
                          {message.userName?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="message-content">
                    {message.userId !== user?.id && (
                      <div className="message-sender">{message.userName}</div>
                    )}
                    <div className="message-bubble">
                      <p>{message.content || message.message}</p>
                      <span className="message-time">{formatTime(message.dateEnvoie || message.timestamp || message.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Écrivez votre message..."
          disabled={sending}
        />
        <button type="submit" disabled={!newMessage.trim() || sending}>
          {sending ? '⏳' : '➤'}
        </button>
      </form>
    </div>
  );
};

export default RoomChat;
